import {
    closestCenter,
    DndContext,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    type DragEndEvent,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useEffect, useMemo, useState, type HTMLAttributes } from 'react';
import { ArrowRight, GripVertical, Search, X } from 'lucide-react';

import { Button } from 'components/ui/button';
import { Checkbox } from 'components/ui/checkbox';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from 'components/ui/dialog';
import { Input } from 'components/ui/input';
import { cn } from 'lib/utils';

import {
    getReportColumnById,
    type ReportColumnDefinition,
    type ReportColumnCategory,
} from './reportTableTypes';

type CustomizeReportColumnsModalProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    columnDefinitions: ReportColumnDefinition<Record<string, unknown>>[];
    categories: ReportColumnCategory[];
    defaultColumnIds: string[];
    selectedColumnIds: string[];
    onApply: (columnIds: string[]) => void;
};

type SelectedColumn = ReportColumnDefinition<Record<string, unknown>>;

type ColumnRowProps = {
    column: SelectedColumn;
    onRemove: (columnId: string) => void;
    dragHandleProps?: HTMLAttributes<HTMLButtonElement>;
    isDragging?: boolean;
};

function ColumnRow({ column, onRemove, dragHandleProps, isDragging = false }: ColumnRowProps) {
    return (
        <div
            className={cn(
                'flex items-center gap-2 rounded-md border border-border bg-card px-2 py-2.5 shadow-sm',
                isDragging
                    ? 'relative z-50 cursor-grabbing shadow-md ring-2 ring-primary/20'
                    : 'transition-shadow hover:bg-muted/70',
            )}
        >
            <button
                type="button"
                className="shrink-0 touch-none appearance-none border-0 bg-transparent p-0 text-muted-foreground shadow-none"
                style={{ cursor: 'grab' }}
                aria-label={`Drag ${column.label}`}
                {...dragHandleProps}
            >
                <GripVertical className="h-4 w-4" />
            </button>
            {/* min-w-0 + truncate: long labels used to overflow and steal clicks from the remove X */}
            <span className="min-w-0 flex-1 truncate text-sm text-foreground">{column.label}</span>
            <Button
                type="button"
                variant="ghost"
                size="icon"
                className="relative z-10 h-8 w-8 shrink-0 text-muted-foreground hover:bg-muted hover:text-foreground"
                style={{ cursor: 'pointer' }}
                // Keep remove clicks out of dnd-kit pointer handling on the row/handle.
                onPointerDown={(event) => event.stopPropagation()}
                onClick={(event) => {
                    event.stopPropagation();
                    onRemove(column.id);
                }}
                aria-label={`Remove ${column.label}`}
            >
                <X className="h-4 w-4" />
            </Button>
        </div>
    );
}

type SortableColumnItemProps = {
    column: SelectedColumn;
    onRemove: (columnId: string) => void;
};

function SortableColumnItem({ column, onRemove }: SortableColumnItemProps) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: column.id,
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition: isDragging ? undefined : transition,
    };

    return (
        <div ref={setNodeRef} style={style}>
            <ColumnRow
                column={column}
                onRemove={onRemove}
                dragHandleProps={{ ...attributes, ...listeners }}
                isDragging={isDragging}
            />
        </div>
    );
}

export function CustomizeReportColumnsModal({
    open,
    onOpenChange,
    columnDefinitions,
    categories,
    defaultColumnIds,
    selectedColumnIds,
    onApply,
}: CustomizeReportColumnsModalProps) {
    const [searchQuery, setSearchQuery] = useState('');
    const [checkedAvailableIds, setCheckedAvailableIds] = useState<string[]>([]);
    const [draftSelectedIds, setDraftSelectedIds] = useState<string[]>(selectedColumnIds);

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 6,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        }),
    );

    useEffect(() => {
        if (open) {
            setDraftSelectedIds(selectedColumnIds);
            setCheckedAvailableIds([]);
            setSearchQuery('');
        }
    }, [open, selectedColumnIds]);

    const availableColumns = useMemo(
        () => columnDefinitions.filter((column) => !draftSelectedIds.includes(column.id)),
        [columnDefinitions, draftSelectedIds],
    );

    const filteredAvailableColumns = useMemo(() => {
        const query = searchQuery.trim().toLowerCase();
        if (!query) {
            return availableColumns;
        }

        return availableColumns.filter((column) => column.label.toLowerCase().includes(query));
    }, [availableColumns, searchQuery]);

    const groupedAvailableColumns = useMemo(
        () =>
            categories
                .map((category) => ({
                    category,
                    columns: filteredAvailableColumns.filter(
                        (column) => column.category === category,
                    ),
                }))
                .filter((group) => group.columns.length > 0),
        [categories, filteredAvailableColumns],
    );

    const selectedColumns = useMemo(
        () =>
            draftSelectedIds
                .map((columnId) => getReportColumnById(columnDefinitions, columnId))
                .filter((column): column is SelectedColumn => column !== undefined),
        [columnDefinitions, draftSelectedIds],
    );

    const toggleAvailableColumn = (columnId: string, checked: boolean) => {
        setCheckedAvailableIds((current) =>
            checked ? [...current, columnId] : current.filter((id) => id !== columnId),
        );
    };

    const addCheckedColumns = () => {
        if (!checkedAvailableIds.length) {
            return;
        }

        setDraftSelectedIds((current) => [...current, ...checkedAvailableIds]);
        setCheckedAvailableIds([]);
    };

    const removeSelectedColumn = (columnId: string) => {
        setDraftSelectedIds((current) => current.filter((id) => id !== columnId));
        setCheckedAvailableIds((current) => current.filter((id) => id !== columnId));
    };

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;

        if (!over || active.id === over.id) {
            return;
        }

        setDraftSelectedIds((current) => {
            const oldIndex = current.indexOf(active.id as string);
            const newIndex = current.indexOf(over.id as string);

            if (oldIndex === -1 || newIndex === -1) {
                return current;
            }

            return arrayMove(current, oldIndex, newIndex);
        });
    };

    const handleReset = () => {
        setDraftSelectedIds(defaultColumnIds);
        setCheckedAvailableIds([]);
        setSearchQuery('');
    };

    const handleCancel = () => {
        setDraftSelectedIds(selectedColumnIds);
        setCheckedAvailableIds([]);
        setSearchQuery('');
        onOpenChange(false);
    };

    const handleApply = () => {
        if (!draftSelectedIds.length) {
            return;
        }

        onApply(draftSelectedIds);
        onOpenChange(false);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="reports-light-overlay flex max-h-[90vh] w-[calc(100%-2rem)] flex-col gap-0 overflow-hidden p-0 sm:max-w-3xl">
                <DialogHeader className="shrink-0 space-y-1 border-b border-border px-6 py-5 text-left">
                    <DialogTitle className="text-xl font-semibold text-foreground">
                        Customize Report Columns
                    </DialogTitle>
                    <DialogDescription className="text-sm text-muted-foreground">
                        Pick the columns you want in your report. You can drag selected columns to
                        change their order.
                    </DialogDescription>
                </DialogHeader>

                <div className="flex min-h-0 flex-1 flex-col overflow-hidden md:flex-row">
                    <div className="flex min-h-0 min-w-0 flex-1 flex-col border-b border-border md:border-b-0 md:border-r">
                        <div className="shrink-0 border-b border-border bg-muted/30 px-5 py-4">
                            <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                                Available Columns
                            </p>
                            <div className="relative mt-3">
                                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                                <Input
                                    value={searchQuery}
                                    onChange={(event) => setSearchQuery(event.target.value)}
                                    placeholder="Search columns..."
                                    className="h-9 border-border bg-card pl-9 shadow-sm"
                                />
                            </div>
                        </div>

                        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
                            {groupedAvailableColumns.length ? (
                                groupedAvailableColumns.map((group) => (
                                    <div key={group.category} className="mb-5 last:mb-0">
                                        <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                                            {group.category}
                                        </p>
                                        <div className="space-y-0.5">
                                            {group.columns.map((column) => {
                                                const checked = checkedAvailableIds.includes(
                                                    column.id,
                                                );

                                                return (
                                                    <label
                                                        key={column.id}
                                                        className="flex cursor-pointer items-center gap-3 rounded-md px-2 py-2.5 transition-colors hover:bg-muted/70"
                                                    >
                                                        <Checkbox
                                                            checked={checked}
                                                            onCheckedChange={(value) =>
                                                                toggleAvailableColumn(
                                                                    column.id,
                                                                    value === true,
                                                                )
                                                            }
                                                        />
                                                        <span className="text-sm font-normal text-foreground">
                                                            {column.label}
                                                        </span>
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <p className="px-2 py-8 text-center text-sm text-muted-foreground">
                                    No matching fields.
                                </p>
                            )}
                        </div>
                    </div>

                    <div className="flex shrink-0 items-center justify-center border-b border-border px-4 py-5 md:border-b-0">
                        <div className="flex flex-col items-center gap-2">
                            <Button
                                type="button"
                                size="icon"
                                className="h-10 w-10 shrink-0 rounded-md shadow-sm"
                                disabled={checkedAvailableIds.length === 0}
                                onClick={addCheckedColumns}
                                aria-label="Add to report"
                            >
                                <ArrowRight className="h-4 w-4 rotate-90 md:rotate-0" />
                            </Button>
                            <span className="max-w-[4.5rem] text-center text-[11px] font-medium leading-tight text-muted-foreground">
                                Add to report
                            </span>
                        </div>
                    </div>

                    <div className="flex min-h-0 min-w-0 flex-1 flex-col md:border-l md:border-border">
                        <div className="shrink-0 border-b border-border bg-muted/30 px-5 py-4">
                            <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                                Selected Columns
                            </p>
                            <p className="mt-1.5 text-xs text-muted-foreground">
                                These columns will appear in your report. ({selectedColumns.length}{' '}
                                columns)
                            </p>
                        </div>

                        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
                            {selectedColumns.length ? (
                                <DndContext
                                    sensors={sensors}
                                    collisionDetection={closestCenter}
                                    modifiers={[restrictToVerticalAxis]}
                                    onDragEnd={handleDragEnd}
                                >
                                    <SortableContext
                                        items={draftSelectedIds}
                                        strategy={verticalListSortingStrategy}
                                    >
                                        <div className="flex flex-col gap-2">
                                            {selectedColumns.map((column) => (
                                                <SortableColumnItem
                                                    key={column.id}
                                                    column={column}
                                                    onRemove={removeSelectedColumn}
                                                />
                                            ))}
                                        </div>
                                    </SortableContext>
                                </DndContext>
                            ) : (
                                <p className="px-2 py-8 text-center text-sm text-muted-foreground">
                                    No columns selected.
                                </p>
                            )}
                        </div>
                    </div>
                </div>

                <DialogFooter className="shrink-0 flex-col gap-3 border-t border-border bg-card px-6 py-4 sm:flex-row sm:justify-end sm:gap-2">
                    <Button
                        type="button"
                        variant="outline"
                        className="hidden sm:inline-flex"
                        onClick={handleReset}
                    >
                        Reset to Default
                    </Button>
                    <Button
                        type="button"
                        variant="outline"
                        className="hidden sm:inline-flex"
                        onClick={handleCancel}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="button"
                        className="w-full sm:w-auto"
                        onClick={handleApply}
                        disabled={draftSelectedIds.length === 0}
                    >
                        Apply Changes
                    </Button>
                    <button
                        type="button"
                        className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground sm:hidden"
                        onClick={handleCancel}
                    >
                        Cancel
                    </button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
